/**
 * Custom Lexical node for blurb embed elements
*/

import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { $createParagraphNode, $getSelection, $isRangeSelection, type ElementFormatType, type LexicalNode, type NodeKey } from 'lexical';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from "yup";

import { SystemDialogContent } from '@cms/components/data/Dialog';
import { ControlledSystemTextInput } from '@cms/components/data/TextInput';
import { TStoryContentElementTypeCustomEmbed, TStoryContentElementTypeCustomEmbedBlurb } from '@dl/declarations';
import { MarkdownFormatter, MarkdownTutorial } from '@dl/ui/components/shared/MarkdownFormatter';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ControlledDropdown } from '../../data/Dropdown';
import { CustomEmbedNode, SerializeEmbedNode } from './CustomEmbedNode';
import { CustomEmbedPluginActions, CustomEmbedPluginContainer, CustomEmbedPluginNote } from './CustomEmbedPluginContainer';

type TBaseType = TStoryContentElementTypeCustomEmbed;
type TExtendedType = TStoryContentElementTypeCustomEmbedBlurb;

const TEXT_ALIGN = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
}

interface EmbedBlurbProps {
    config: TBaseType
}

export const EmbedBlurb: React.FC<EmbedBlurbProps> = ({ config }) => {
    const { text = '', align = 'left' } = (config as TExtendedType).embed.config

    return (
        <div className="rounded-r-md border-l-4 border-blue-100 bg-primary-25 text-[16.8px] leading-[170%] dark:bg-primary-700">
            <div
                className={clsx(
                    'px-6 py-4 text-[#0D1E3C] dark:text-white [&>p:last-child]:mb-0',
                    TEXT_ALIGN?.[align]
                )}
            >
                <MarkdownFormatter text={text} />
            </div>
        </div>
    )
}

export const EditEmbedBlurb: React.FC<
    EmbedBlurbProps & {
        handleSubmit?: (payload: TExtendedType) => void;
    }
> = ({ config, handleSubmit }) => {
    const usedConfig = config as TExtendedType;

    const [editor] = useLexicalComposerContext();

    const methods = useForm({
        resolver: yupResolver(yup.object().shape({
            text: yup.string().required().label("Text"),
            align: yup.string().oneOf(['left', 'center', 'right']).default('left').label("Text Alignment"),
        })),
        defaultValues: {
            text: usedConfig.embed.config.text,
            align: usedConfig.embed.config.align || 'left',
        }
    })

    const { handleSubmit: handleFormSubmit } = methods;

    const handleSubmitEdits = handleFormSubmit((data) => {
        editor.update(() => {
            const payload: TExtendedType = {
                ...usedConfig,
                embed: {
                    ...usedConfig.embed,
                    config: {
                        ...usedConfig.embed.config,
                        ...data
                    }
                }
            }

            if (handleSubmit) {
                handleSubmit(payload)
            }
            else {

                const selection = $getSelection();

                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const node = $createBlurbNode(payload as TStoryContentElementTypeCustomEmbed);
                    selection.insertNodes([node, $createParagraphNode()]);
                }
            }
        })
    })

    return (
        <CustomEmbedPluginContainer
            embedTitle='Embed/Embed Blurb'
            embedDescription={(
                <>
                    <b>Add textual content to give more context</b> about the content our readers are consuming in our articles.
                </>
            )}
            instructionsScreenshotUrl='/images/embeds/blurb.png'
            instructionsNotesComponent={(
                <div className="w-full">
                    <CustomEmbedPluginNote
                        wrapperClassName='w-full flex items-center gap-8'
                        notes={[
                            {
                                title: "Use markdown to add the following elements",
                                description: <MarkdownTutorial showTitle={false} />
                            }
                        ]}
                    />
                </div>
            )}
            embedActionComponent={(
                <FormProvider {...methods}>
                    <form className="flex flex-col gap-4">

                        <ControlledSystemTextInput
                            name='text'
                            inputSize='full'
                            placeholder="Insert text blurb here"
                            multiline
                            rows={5}
                        />

                        <ControlledDropdown
                            name='align'
                            options={[
                                { _id: '1', label: 'Left', slug: 'left', groupKey: 'Options' },
                                { _id: '2', label: 'Center', slug: 'center', groupKey: 'Options' },
                                { _id: '3', label: 'Right', slug: 'right', groupKey: 'Options' }
                            ]}
                            inputSize='full'
                            valueKey='slug'
                            label='Text align'
                            allowSingleDeselect={false}
                        />

                        <CustomEmbedPluginActions
                            handleSubmit={handleSubmitEdits}
                        />
                    </form>
                </FormProvider>
            )}
        />
    );
}

export const EditEmbedBlurbDialog: React.FC<
    EmbedBlurbProps
> = ({ config }) => {
    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Embed/Blurb'
            }}
            content={{
                component: (
                    <EditEmbedBlurb config={config} />
                ),
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}

export class CustomBlurbNode extends CustomEmbedNode {
    __config: TBaseType;

    constructor(
        config: TBaseType,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(
            "Embed Blurb",
            <EmbedBlurb config={config} />,
            <EditEmbedBlurb config={config} handleSubmit={(payload) => { this.handleSubmit(payload) }} />,
            config,
            format,
            key
        );
        this.__config = config;
    }

    handleSubmit(config: TExtendedType) {
        this.setConfig(config as TBaseType);
    }

    static getType(): string {
        return "blurb"
    }

    static clone(node: CustomBlurbNode): CustomBlurbNode {
        return new CustomBlurbNode(node.__config, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializeEmbedNode): CustomBlurbNode {
        return $createBlurbNode(
            serializedNode.config,
        ).updateFromJSON(
            serializedNode,
        );
    }
}

export function $createBlurbNode(
    config: TBaseType,
): CustomBlurbNode {
    return new CustomBlurbNode(config);
}

export function $isBlurbNode(
    node: CustomBlurbNode | LexicalNode | null | undefined,
): node is CustomBlurbNode {
    return node instanceof CustomBlurbNode;
}
